import React, { useState, useEffect } from 'react';
import { consoleLogInDev, formatAddress } from 'shared';
import { Colors } from 'library';
import { Card, Chip, EmptyText, ResponsiveRow, Row, Stack, Text, TextButton } from 'library/ui';
import * as fromApi from './draftApi';
import { EditDraft } from '../EditDraft';
import DeleteIcon from '@mui/icons-material/Delete';
import { DraftJob } from './types';
import { useUserContext, PermissionType } from 'UserContext';

export default function DraftList() {
    const { hasPermission } = useUserContext();
    const [ongoingRequests, setOngoingRequests] = useState<DraftJob[]>([]);
    const [loading, setLoading] = useState(false);

    const getDrafts = () => {
        return fromApi
            .getDrafts()
            .then((response) => {
                setOngoingRequests(structuredClone(response));
            })
            .catch(consoleLogInDev)
            .finally(() => {
                setLoading(false);
            });
    };

    const deleteDraft = (draftId: number) => {
        return fromApi
            .deleteDraft(draftId)
            .then(() => {
                getDrafts();
            })
            .catch(consoleLogInDev);
    };

    useEffect(() => {
        setLoading(true);
        getDrafts();
    }, []);

    function canEditDraft(addressId: number) {
        return hasPermission(PermissionType.PostJob, addressId);
    }

    if (loading) return <EmptyText>Loading...</EmptyText>;

    return (
        <Stack gap="sm">
            {!ongoingRequests.length ? (
                <Text>You have no draft jobs to finish. You&apos;re all caught up!</Text>
            ) : (
                ongoingRequests.map((ongoingRequest) => (
                    <Card key={ongoingRequest.id}>
                        <Stack gap="sm">
                            <Row gap="sm" align="center" wrap>
                                <Text size="md" weight="bold">
                                    {ongoingRequest.headline}
                                </Text>
                                <Chip variant="filled" bgColor={Colors.accent} textColor={Colors.white}>
                                    Draft
                                </Chip>
                            </Row>
                            <Text size="sm">{formatAddress(ongoingRequest.address)}</Text>
                            {canEditDraft(ongoingRequest.address.id) && (
                                <ResponsiveRow gap="md" collapseAt="md" align="center">
                                    <EditDraft jobId={ongoingRequest.id} onClose={getDrafts} />
                                    <TextButton onClick={() => deleteDraft(ongoingRequest.id)}>
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                        Delete Draft
                                    </TextButton>
                                </ResponsiveRow>
                            )}
                        </Stack>
                    </Card>
                ))
            )}
        </Stack>
    );
}
